import { useState, useEffect, lazy, Suspense } from 'react';
import PropTypes from 'prop-types';
import FingerprintJS from '@fingerprintjs/fingerprintjs';
import { Typography, Box, Link, CircularProgress } from '@mui/material';
import { keyframes } from '@emotion/react';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import logger from '../utils/logger';
import { useAuth } from '../provider/AuthProvider';
import MainMenu from '../components/MainMenu';
import httpProvider from '../provider/HttpProvider';

const CustomAlert = lazy(() => import('../components/CustomAlert'));
const Video = lazy(() => import('../components/Video'));

const slideInFromTop = keyframes`
  0% { transform: translateY(-100%); opacity: 0; }
  100% { transform: translateY(0); opacity: 1; }
`;

const fadeIn = keyframes`
  0% { opacity: 0; transform: translateY(20px); }
  100% { opacity: 1; transform: translateY(0); }
`;

const StatItem = ({ label, value }) => (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.3 }}>
        <Typography sx={{ fontSize: '0.65rem', letterSpacing: '0.15em', color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase' }}>
            {label}
        </Typography>
        <Typography sx={{ fontSize: '1.4rem', fontWeight: 700, color: 'white', lineHeight: 1 }}>
            {value ?? '—'}
        </Typography>
    </Box>
);

StatItem.propTypes = {
    label: PropTypes.string.isRequired,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

const Sep = () => (
    <Box sx={{ width: '1px', height: 32, background: 'rgba(255,255,255,0.1)', alignSelf: 'center' }} />
);

const Home = () => {
    const [alertInfo] = useState({ message: '', severity: 'info' });
    const [loading, setLoading] = useState(true);
    const [visitorId, setVisitorId] = useState('');
    const [bookmarkStats, setBookmarkStats] = useState(null);
    const [tagStats, setTagStats] = useState(null);
    const { getToken } = useAuth();
    const token = getToken();

    useEffect(() => {
        const loadFingerprint = async () => {
            try {
                const fp = await FingerprintJS.load();
                const result = await fp.get();
                setVisitorId(result.visitorId);
            } catch (error) {
                logger.error('Error loading fingerprint:', error);
            } finally {
                setLoading(false);
            }
        };
        loadFingerprint();
    }, []);

    useEffect(() => {
        if (!token) return;
        Promise.all([
            httpProvider.get('/api/bookmarks/stats'),
            httpProvider.get('/api/tags/stats'),
        ]).then(([bStats, tStats]) => {
            setBookmarkStats(bStats ?? {});
            setTagStats(tStats ?? {});
        }).catch(() => {});
    }, [token]);

    return (
        <Box
            sx={{
                height: '100vh',
                width: '100vw',
                position: 'relative',
                overflow: 'hidden',
            }}
        >
            {/* Fixed Video Background */}
            <Box
                sx={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    width: '100vw',
                    height: '100vh',
                    zIndex: 0,
                    overflow: 'hidden',
                }}
            >
                <Suspense
                    fallback={
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                minHeight: '100vh',
                                width: '100%',
                                backgroundColor: 'rgba(0,0,0,0.9)',
                            }}
                        >
                            <CircularProgress />
                        </Box>
                    }
                >
                    <Video />
                </Suspense>
            </Box>

            {/* Alert on top of video */}
            <Suspense fallback={null}>
                <CustomAlert
                    message={alertInfo.message}
                    severity={alertInfo.severity}
                />
            </Suspense>

            {/* Top Navigation */}
            {token ? (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        right: 0,
                        padding: '8px 16px',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(10px)',
                        zIndex: 1000,
                        animation: `${slideInFromTop} 0.5s ease-out`,
                        display: 'flex',
                        justifyContent: 'flex-end',
                        alignItems: 'center',
                    }}
                >
                    <MainMenu />
                </Box>
            ) : (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 20,
                        right: 20,
                        zIndex: 1000,
                    }}
                >
                    <Link
                        href="/login"
                        color="inherit"
                        variant="body2"
                        sx={{
                            fontSize: '0.75rem',
                            textDecoration: 'none',
                            color: 'white',
                            padding: '8px 16px',
                            backgroundColor: 'rgba(0, 0, 0, 0.7)',
                            backdropFilter: 'blur(10px)',
                            borderRadius: '4px',
                            '&:hover': {
                                backgroundColor: 'rgba(0, 0, 0, 0.85)',
                            },
                        }}
                    >
                        Login
                    </Link>
                </Box>
            )}

            {/* Hero Section */}
            {token && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 0,
                        left: 0,
                        width: '100vw',
                        height: '100vh',
                        zIndex: 1,
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        px: 8,
                        animation: `${fadeIn} 0.8s ease-out`,
                    }}
                >
                    <Box
                        sx={{
                            mt: 4,
                            px: 3,
                            py: 2,
                            borderRadius: 3,
                            background: 'rgba(0,0,0,0.45)',
                            backdropFilter: 'blur(12px)',
                            border: '1px solid rgba(255,255,255,0.08)',
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 2,
                            alignItems: 'center',
                            animation: `${fadeIn} 1s ease-out`,
                        }}
                    >
                        {bookmarkStats ? (
                            <>
                                <StatItem label="Bookmarks" value={bookmarkStats.total} />
                                <Sep />
                                <StatItem label="+7d" value={bookmarkStats.recent_7d} />
                                <Sep />
                                <StatItem label="+30d" value={bookmarkStats.recent_30d} />
                                <Sep />
                                <StatItem label="Tags" value={tagStats?.total} />
                            </>
                        ) : (
                            <Typography sx={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.3)' }}>
                                Loading stats…
                            </Typography>
                        )}
                    </Box>
                </Box>
            )}

            {/* Footer with Visitor ID */}
            {!loading && token && (
                <Box
                    sx={{
                        position: 'fixed',
                        bottom: 0,
                        left: 0,
                        margin: '0 0 0 1em',
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        backdropFilter: 'blur(10px)',
                        color: 'white',
                        borderRadius: '8px 8px 0 0',
                        padding: '8px 16px',
                        animation: `${slideInFromTop} 0.5s ease-out`,
                        zIndex: 1000,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                    }}
                >
                    <FingerprintIcon
                        sx={{ fontSize: '1.2rem', color: 'white' }}
                    />
                    <Typography variant="body2" component="p">
                        {visitorId}
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default Home;
